/**
 * Parse UTM parameters from URL search params
 * Extracts all parameters that start with 'utm_'
 */
export function parseUtmParams(): Record<string, string> {
  const searchParams = new URLSearchParams(window.location.search)
  const utmParams: Record<string, string> = {}

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('utm_')) {
      utmParams[key] = value
    }
  }

  return utmParams
}

export interface AcquisitionData {
  utmParams?: Record<string, string>
  referrer?: string
  landingPage?: string
  timestamp: string
  device: {
    screenResolution: string
    language: string
    timeZone: string
  }
}

/**
 * Capture comprehensive acquisition data for marketing attribution
 * Includes UTM params, referrer, device context, and more
 */
export function captureAcquisitionData(): AcquisitionData {
  const utmParams = parseUtmParams()

  return {
    ...(Object.keys(utmParams).length > 0 ? { utmParams } : {}),
    ...(document.referrer ? { referrer: document.referrer } : {}),
    landingPage: window.location.href,
    timestamp: new Date().toISOString(),
    device: {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  }
}

const ACQUISITION_DATA_KEY = 'acquisitionData'

/**
 * Get acquisition data from sessionStorage
 */
export function getAcquisitionDataFromStorage(): AcquisitionData | undefined {
  try {
    const stored = sessionStorage.getItem(ACQUISITION_DATA_KEY)
    if (stored) {
      return JSON.parse(stored) as AcquisitionData
    }
  } catch {
    return undefined
  }
}

/**
 * Store acquisition data to sessionStorage
 * Should be called once on app initialization
 */
export function storeAcquisitionData(data: AcquisitionData): void {
  try {
    sessionStorage.setItem(ACQUISITION_DATA_KEY, JSON.stringify(data))
  } catch {
    // Silent fail - sessionStorage might be disabled
  }
}
